/** 将编辑器内 HTML 转为供大模型审查的 Markdown（无多模态：图片保留为引用说明） */

import { applyTextReplacementsInHtml, replaceTextInHtml } from '@/utils/htmlTextAnchor';

function escapeMdCell(s: string) {
  return s.replace(/\|/g, '\\|').replace(/\r?\n/g, ' ').trim();
}

function tableToMarkdown(table: HTMLTableElement): string {
  const rows = Array.from(table.querySelectorAll('tr'));
  const lines: string[] = [];
  let maxCols = 0;
  const grid: string[][] = [];
  for (const tr of rows) {
    const cells = Array.from(tr.querySelectorAll('th,td'));
    const texts = cells.map((c) => escapeMdCell(c.textContent || ''));
    maxCols = Math.max(maxCols, texts.length);
    grid.push(texts);
  }
  if (!grid.length) return '';
  for (const r of grid) {
    while (r.length < maxCols) r.push('');
  }
  const sep = Array(maxCols).fill('---');
  lines.push(`| ${grid[0].join(' | ')} |`);
  lines.push(`| ${sep.join(' | ')} |`);
  for (let i = 1; i < grid.length; i += 1) {
    lines.push(`| ${grid[i].join(' | ')} |`);
  }
  return lines.join('\n');
}

function walkBlock(node: Node, parts: string[]) {
  if (node.nodeType === Node.TEXT_NODE) {
    const t = (node.textContent || '').replace(/\s+/g, ' ').trim();
    if (t) parts.push(t);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();
  if (tag === 'script' || tag === 'style' || tag === 'iframe') return;
  if (tag === 'table') {
    const md = tableToMarkdown(el as HTMLTableElement);
    if (md) {
      parts.push('\n');
      parts.push(md);
      parts.push('\n');
    }
    return;
  }
  if (tag === 'br') {
    parts.push('\n');
    return;
  }
  if (tag === 'img') {
    const alt = (el.getAttribute('alt') || el.getAttribute('title') || '图示').trim();
    parts.push(`\n![${alt}](图表引用:内嵌图片，审查以 surrounding 正文为准)\n`);
    return;
  }
  if (['p', 'div', 'section', 'article', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'td', 'th'].includes(tag)) {
    for (const c of Array.from(el.childNodes)) walkBlock(c, parts);
    if (['p', 'div', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre'].includes(tag)) parts.push('\n\n');
    return;
  }
  for (const c of Array.from(el.childNodes)) walkBlock(c, parts);
}

export function htmlToReviewMarkdown(html: string): string {
  if (!html || !html.trim()) return '';
  try {
    const doc = new DOMParser().parseFromString(`<div id="aipd-md-root">${html}</div>`, 'text/html');
    const root = doc.getElementById('aipd-md-root');
    if (!root) return html.replace(/<[^>]+>/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
    const parts: string[] = [];
    for (const c of Array.from(root.childNodes)) walkBlock(c, parts);
    return parts
      .join('')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/[ \t]+\n/g, '\n')
      .trim();
  } catch {
    return html.replace(/<[^>]+>/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  }
}

export interface DocumentReviewFinding {
  id: string;
  category: string;
  anchor_text: string;
  suggested_replacement: string;
  rationale?: string;
}

export function applyDocumentReviewFindingsToHtml(
  html: string,
  findings: Array<Pick<DocumentReviewFinding, 'anchor_text' | 'suggested_replacement'>>,
): string {
  const { html: out } = applyTextReplacementsInHtml(
    html,
    findings.map((f) => ({
      anchor_text: f.anchor_text,
      suggested_replacement: f.suggested_replacement ?? '',
    })),
  );
  return out;
}

/** 将已采纳的修改建议还原为原文（仅替换首次匹配） */
export function revertDocumentReviewFindingInHtml(
  html: string,
  anchorText: string,
  suggestedReplacement: string,
): string {
  const { html: out, replaced } = replaceTextInHtml(html, suggestedReplacement, anchorText);
  return replaced ? out : html;
}
